import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { PageHero } from "./About";
import { useList } from "@/components/Blocks";
import { useSite } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { useSeo, track } from "@/lib/analytics";

export const COUNTRIES = ["Costa Rica", "Panamá", "Nicaragua", "Honduras", "El Salvador", "Guatemala", "México", "Colombia", "República Dominicana", "Perú", "Chile", "Ecuador", "Estados Unidos", "Otro"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const Field = ({ label, error, children, required }) => (
  <label className="block space-y-1.5">
    <span className="field-label">{label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
    {error && <span className="block text-xs text-red-600">{error}</span>}
  </label>
);

export const NativeSelect = (props) => (
  <select {...props} className="flex h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--cc-blue)]" />
);

function useForm(initial, rules) {
  const [v, setV] = useState(initial);
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const validate = () => {
    const er = {};
    Object.entries(rules).forEach(([k, fn]) => { const m = fn(v[k], v); if (m) er[k] = m; });
    setErrors(er);
    return !Object.keys(er).length;
  };
  return { v, set, errors, validate, setV };
}

const req = (n = 2) => (x) => (!x || String(x).trim().length < n ? "Campo requerido" : null);
const email = (x) => (!EMAIL_RE.test(x || "") ? "Correo inválido" : null);
const consent = (x) => (!x ? "Debe aceptar la política de privacidad" : null);

const Done = ({ title, text, testid }) => (
  <div className="card-cc text-center" data-testid={testid}>
    <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
    <h2 className="mt-4 font-display text-2xl font-semibold">{title}</h2>
    <p className="mt-2 text-slate-600">{text}</p>
  </div>
);

const Consent = ({ checked, onChange, error, id }) => (
  <div>
    <label className="flex items-start gap-3 text-sm text-slate-600">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={id} className="mt-0.5" />
      <span>Acepto la <Link to="/legal/privacy" className="text-brand underline">política de privacidad</Link> y el tratamiento de mis datos para atender esta solicitud.</span>
    </label>
    {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
  </div>
);

export function ContactPage() {
  const { site } = useSite();
  const [sp] = useSearchParams();
  const products = useList("/public/products") || [];
  const services = useList("/public/services") || [];
  const f = useForm({ name: "", company: "", email: "", phone: "", country: "Costa Rica", interest: sp.get("interes") || "", message: "", consent: false, website: "" },
    { name: req(), email, country: req(), message: req(10), consent });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useSeo({ title: "Contacto", description: "Contacte a Cloud Center Costa Rica para desarrollar su próxima solución tecnológica." });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.validate()) return;
    setBusy(true);
    try {
      await api.post("/contact", f.v);
      track("ContactSubmitted", { interest: f.v.interest });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const interests = [...products.map((p) => p.name), ...services.map((s) => s.title)];
  return (
    <>
      <PageHero eyebrow="Contacto" title="Conversemos sobre su próximo proyecto" sub="Cuéntenos el reto operativo de su organización. Un especialista le responderá a la brevedad." />
      <section className="wrap grid gap-12 py-16 md:py-24 lg:grid-cols-[1fr_1.4fr]">
        <aside className="space-y-6" data-testid="contact-channels">
          <p className="eyebrow">Canales</p>
          {site.email && <a href={`mailto:${site.email}`} className="flex items-center gap-3 text-slate-700 hover:text-brand"><Mail className="h-5 w-5 text-brand" />{site.email}</a>}
          {(site.phones || []).map((p) => <a key={p} href={`tel:${p.replace(/\s|-/g, "")}`} className="flex items-center gap-3 text-slate-700 hover:text-brand"><Phone className="h-5 w-5 text-brand" />{p}</a>)}
          {site.website && <p className="flex items-center gap-3 text-slate-700"><Globe className="h-5 w-5 text-brand" />{site.website}</p>}
        </aside>
        {done ? <Done testid="contact-success" title="¡Solicitud recibida!" text="Gracias por escribirnos. Nuestro equipo le contactará pronto." /> : (
          <form onSubmit={submit} noValidate className="card-cc grid gap-5 sm:grid-cols-2" data-testid="contact-form">
            <input type="text" name="website" value={f.v.website} onChange={f.set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <Field label="Nombre" required error={f.errors.name}><Input value={f.v.name} onChange={f.set("name")} data-testid="contact-name" /></Field>
            <Field label="Empresa"><Input value={f.v.company} onChange={f.set("company")} data-testid="contact-company" /></Field>
            <Field label="Correo electrónico" required error={f.errors.email}><Input type="email" value={f.v.email} onChange={f.set("email")} data-testid="contact-email" /></Field>
            <Field label="Teléfono"><Input value={f.v.phone} onChange={f.set("phone")} data-testid="contact-phone" /></Field>
            <Field label="País" required error={f.errors.country}><NativeSelect value={f.v.country} onChange={f.set("country")} data-testid="contact-country">{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</NativeSelect></Field>
            <Field label="Servicio o producto de interés">
              <NativeSelect value={f.v.interest} onChange={f.set("interest")} data-testid="contact-interest">
                <option value="">Seleccione…</option>
                {f.v.interest && !interests.includes(f.v.interest) && <option>{f.v.interest}</option>}
                {interests.map((i) => <option key={i}>{i}</option>)}
              </NativeSelect>
            </Field>
            <div className="sm:col-span-2"><Field label="Mensaje" required error={f.errors.message}><Textarea rows={5} value={f.v.message} onChange={f.set("message")} data-testid="contact-message" /></Field></div>
            <div className="sm:col-span-2"><Consent id="contact-consent" checked={f.v.consent} onChange={f.set("consent")} error={f.errors.consent} /></div>
            <button disabled={busy} className="btn-cc sm:col-span-2 disabled:opacity-60" data-testid="contact-submit">{busy ? "Enviando…" : "Enviar solicitud"}</button>
          </form>
        )}
      </section>
    </>
  );
}

export function DemoPage() {
  const [sp] = useSearchParams();
  const products = useList("/public/products") || [];
  const f = useForm({ product: sp.get("producto") || "", name: "", company: "", email: "", country: "Costa Rica", preferred_date: "", comments: "", consent: false, website: "" },
    { product: req(), name: req(), company: req(), email, country: req(), consent });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useSeo({ title: "Solicitar demostración", description: "Agende una demostración comercial de las soluciones de Cloud Center." });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.validate()) return;
    setBusy(true);
    try {
      await api.post("/demo", f.v);
      track("DemoRequested", { product: f.v.product });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const options = [...new Set([...products.map((p) => p.name), "Desarrollo a medida"])];
  return (
    <>
      <PageHero eyebrow="Demostración" title="Solicite una demostración personalizada" sub="Indíquenos el producto y una fecha preferida. Confirmaremos la cita según la disponibilidad del equipo comercial." />
      <section className="wrap max-w-3xl py-16 md:py-24">
        {done ? <Done testid="demo-success" title="Solicitud de demostración recibida" text="Su fecha preferida queda registrada como pendiente. El equipo comercial verificará disponibilidad y le confirmará la cita por correo." /> : (
          <form onSubmit={submit} noValidate className="card-cc grid gap-5 sm:grid-cols-2" data-testid="demo-form">
            <input type="text" name="website" value={f.v.website} onChange={f.set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="sm:col-span-2">
              <Field label="Producto" required error={f.errors.product}>
                <NativeSelect value={f.v.product} onChange={f.set("product")} data-testid="demo-product">
                  <option value="">Seleccione…</option>{options.map((o) => <option key={o}>{o}</option>)}
                </NativeSelect>
              </Field>
            </div>
            <Field label="Nombre" required error={f.errors.name}><Input value={f.v.name} onChange={f.set("name")} data-testid="demo-name" /></Field>
            <Field label="Empresa" required error={f.errors.company}><Input value={f.v.company} onChange={f.set("company")} data-testid="demo-company" /></Field>
            <Field label="Correo" required error={f.errors.email}><Input type="email" value={f.v.email} onChange={f.set("email")} data-testid="demo-email" /></Field>
            <Field label="País" required error={f.errors.country}><NativeSelect value={f.v.country} onChange={f.set("country")} data-testid="demo-country">{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</NativeSelect></Field>
            <Field label="Fecha preferida"><Input type="date" min={new Date().toISOString().slice(0, 10)} value={f.v.preferred_date} onChange={f.set("preferred_date")} data-testid="demo-date" /></Field>
            <div className="sm:col-span-2"><Field label="Comentarios"><Textarea rows={4} value={f.v.comments} onChange={f.set("comments")} data-testid="demo-comments" /></Field></div>
            <div className="sm:col-span-2"><Consent id="demo-consent" checked={f.v.consent} onChange={f.set("consent")} error={f.errors.consent} /></div>
            <button disabled={busy} className="btn-cc sm:col-span-2 disabled:opacity-60" data-testid="demo-submit">{busy ? "Enviando…" : "Solicitar demostración"}</button>
          </form>
        )}
      </section>
    </>
  );
}
